import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, X, Trash, ChartBar, Bell, BellRinging, FilePdf, Clock, Printer, FileText,
  Bird, Plant, Tree, PawPrint, Bug, Flower, FirstAidKit, Footprints, Fish, Butterfly, Mountains,
  Storefront
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { API, categoryConfig, templateIcons } from "@/config/constants";
import { generatePDF } from "@/utils/pdf";
import { requestNotificationPermission, scheduleReminder } from "@/utils/notifications";
import { initDB } from "@/utils/offline";

const iconOptions = [
  { value: "file", label: "Document", Icon: FileText },
  { value: "bird", label: "Bird", Icon: Bird },
  { value: "plant", label: "Plant", Icon: Plant },
  { value: "tree", label: "Tree", Icon: Tree },
  { value: "paw", label: "Animal", Icon: PawPrint },
  { value: "mountains", label: "Mountain", Icon: Mountains },
  { value: "bug", label: "Insect", Icon: Bug },
  { value: "flower", label: "Flower", Icon: Flower },
  { value: "fungi", label: "Fungi", Icon: FirstAidKit },
  { value: "snake", label: "Reptile", Icon: Footprints },
  { value: "fish", label: "Fish", Icon: Fish },
  { value: "butterfly", label: "Butterfly", Icon: Butterfly }
];

const StatisticsPage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateTemplate, setShowCreateTemplate] = useState(false);
  const [customTemplates, setCustomTemplates] = useState([]);
  const [newTemplate, setNewTemplate] = useState({ name: "", icon: "file", title: "", content: "", tags: [] });
  const [templateTagInput, setTemplateTagInput] = useState("");
  const [entries, setEntries] = useState([]);
  const [speciesData, setSpeciesData] = useState([]);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [reminderTime, setReminderTime] = useState("09:00");
  const [showReminderDialog, setShowReminderDialog] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, templatesRes, entriesRes, speciesRes] = await Promise.all([
          axios.get(`${API}/species-stats`),
          axios.get(`${API}/templates/custom`),
          axios.get(`${API}/entries`),
          axios.get(`${API}/species-gallery`)
        ]);
        setStats(statsRes.data);
        setCustomTemplates(templatesRes.data);
        setEntries(entriesRes.data);
        setSpeciesData(speciesRes.data);
        if ("Notification" in window) setNotificationsEnabled(Notification.permission === "granted");
        const db = await initDB();
        const savedReminder = await db.get("settings", "reminderTime");
        if (savedReminder) {
          setReminderTime(`${String(savedReminder.hour).padStart(2, '0')}:${String(savedReminder.minute).padStart(2, '0')}`);
          setNotificationsEnabled(savedReminder.enabled);
        }
      } catch (error) { toast.error("Failed to load statistics"); }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const handleExportPDF = async () => {
    setGeneratingPDF(true);
    try { await generatePDF(entries, stats, speciesData); toast.success("PDF exported successfully!"); }
    catch (error) { toast.error("Failed to generate PDF"); }
    finally { setGeneratingPDF(false); }
  };

  const handleEnableNotifications = async () => {
    const permission = await requestNotificationPermission();
    if (permission === "granted") { setNotificationsEnabled(true); setShowReminderDialog(true); toast.success("Notifications enabled!"); }
    else if (permission === "denied") toast.error("Notifications blocked. Please enable in browser settings.");
    else toast.error("Notifications not supported in this browser.");
  };

  const handleSetReminder = async () => {
    const [hour, minute] = reminderTime.split(":").map(Number);
    const scheduledTime = await scheduleReminder(hour, minute);
    setShowReminderDialog(false);
    toast.success(`Daily reminder set for ${scheduledTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`);
  };

  const handleDisableNotifications = async () => {
    const db = await initDB();
    await db.put("settings", { key: "reminderTime", enabled: false });
    setNotificationsEnabled(false);
    toast.info("Reminders disabled");
  };

  const handleCreateTemplate = async () => {
    if (!newTemplate.name || !newTemplate.title) { toast.error("Name and title are required"); return; }
    try {
      const response = await axios.post(`${API}/templates/custom`, newTemplate);
      setCustomTemplates(prev => [...prev, response.data]);
      setNewTemplate({ name: "", icon: "file", title: "", content: "", tags: [] });
      setShowCreateTemplate(false);
      toast.success("Template created!");
    } catch (error) { toast.error("Failed to create template"); }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm("Delete this template?")) return;
    try { await axios.delete(`${API}/templates/custom/${templateId}`); setCustomTemplates(prev => prev.filter(t => t.id !== templateId)); toast.success("Template deleted"); }
    catch (error) { toast.error("Failed to delete template"); }
  };

  const addTemplateTag = () => {
    if (templateTagInput.trim() && !newTemplate.tags.includes(templateTagInput.trim())) {
      setNewTemplate(prev => ({ ...prev, tags: [...prev.tags, templateTagInput.trim()] }));
      setTemplateTagInput("");
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto page-content">
        <Skeleton className="h-10 w-64 mb-6 bg-[#EAE7DE]" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6"><Skeleton className="h-80 bg-[#EAE7DE] rounded-lg" /><Skeleton className="h-80 bg-[#EAE7DE] rounded-lg" /></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto page-content" data-testid="stats-page">
      <div className="flex flex-col gap-3 mb-6">
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-white">Statistics & Templates</h1>
        <div className="grid grid-cols-3 sm:flex sm:flex-wrap gap-2">
          <Dialog open={showReminderDialog} onOpenChange={setShowReminderDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" onClick={notificationsEnabled ? () => setShowReminderDialog(true) : handleEnableNotifications} className="bg-[#55624F] border-[#55624F] text-white text-xs hover:bg-[#424D3D]" data-testid="notification-btn">
                {notificationsEnabled ? <BellRinging size={16} weight="duotone" className="sm:mr-1" /> : <Bell size={16} weight="duotone" className="sm:mr-1" />}<span className="hidden sm:inline">Reminders</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-[#D2C2B3]">
              <DialogHeader><DialogTitle className="font-heading text-xl">Daily Journaling Reminder</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-4">
                <div><label className="block text-sm font-medium text-[#55624F] mb-2">Reminder Time</label><Input type="time" value={reminderTime} onChange={(e) => setReminderTime(e.target.value)} className="bg-[#EAE7DE] border-[#D2C2B3]" /></div>
                <p className="text-sm text-[#7C8068]">You'll receive a daily notification at this time to remind you to document your nature observations.</p>
                <div className="flex gap-3">
                  {notificationsEnabled && (<Button variant="outline" onClick={handleDisableNotifications} className="flex-1 border-[#C25A3C] text-[#C25A3C]">Disable Reminders</Button>)}
                  <Button onClick={handleSetReminder} className="flex-1 bg-[#55624F] hover:bg-[#424D3D] text-white"><Clock size={18} className="mr-2" />Set Reminder</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          <Button onClick={handleExportPDF} disabled={generatingPDF} size="sm" className="bg-[#55624F] hover:bg-[#424D3D] text-white text-xs" data-testid="export-pdf-btn">
            {generatingPDF ? <span className="animate-pulse-soft">...</span> : <><FilePdf size={16} className="sm:mr-1" /><span className="hidden sm:inline">Export PDF</span></>}
          </Button>
          <Button onClick={() => navigate("/print")} variant="outline" size="sm" className="bg-[#55624F] border-[#55624F] text-white text-xs hover:bg-[#424D3D]" data-testid="print-view-btn"><Printer size={16} className="sm:mr-1" /><span className="hidden sm:inline">Print</span></Button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4 mb-8">
        <Card className="bg-white border-[#D2C2B3]"><CardContent className="p-2 sm:p-4 text-center"><p className="text-xl sm:text-3xl font-heading font-semibold text-[#55624F]">{stats?.total_sightings || 0}</p><p className="text-xs sm:text-sm text-[#7C8068]">Total Sightings</p></CardContent></Card>
        <Card className="bg-white border-[#D2C2B3]"><CardContent className="p-2 sm:p-4 text-center"><p className="text-xl sm:text-3xl font-heading font-semibold text-[#C25A3C]">{stats?.unique_species || 0}</p><p className="text-xs sm:text-sm text-[#7C8068]">Unique Species</p></CardContent></Card>
        <Card className="bg-white border-[#D2C2B3]"><CardContent className="p-2 sm:p-4 text-center"><p className="text-xl sm:text-3xl font-heading font-semibold text-[#D99559]">{stats?.categories?.length || 0}</p><p className="text-xs sm:text-sm text-[#7C8068]">Categories</p></CardContent></Card>
        <Card className="bg-white border-[#D2C2B3]"><CardContent className="p-2 sm:p-4 text-center"><p className="text-xl sm:text-3xl font-heading font-semibold text-[#6B8E23]">{stats?.monthly_sightings?.length || 0}</p><p className="text-xs sm:text-sm text-[#7C8068]">Active Months</p></CardContent></Card>
      </div>

      {/* Charts */}
      {stats?.total_sightings > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <Card className="bg-white border-[#D2C2B3]"><CardHeader><CardTitle className="font-heading text-xl text-[#2E332B]">Seasonal Sightings</CardTitle></CardHeader>
            <CardContent><ResponsiveContainer width="100%" height={250}><BarChart data={stats.monthly_sightings}><XAxis dataKey="month" tick={{ fill: '#7C8068', fontSize: 12 }} /><YAxis tick={{ fill: '#7C8068', fontSize: 12 }} /><Tooltip contentStyle={{ backgroundColor: '#F5F4F0', border: '1px solid #D2C2B3' }} /><Bar dataKey="count" fill="#55624F" radius={[4, 4, 0, 0]} /></BarChart></ResponsiveContainer></CardContent>
          </Card>
          <Card className="bg-white border-[#D2C2B3]"><CardHeader><CardTitle className="font-heading text-xl text-[#2E332B]">By Category</CardTitle></CardHeader>
            <CardContent><ResponsiveContainer width="100%" height={250}><PieChart><Pie data={stats.categories} dataKey="count" nameKey="category" cx="50%" cy="50%" outerRadius={80} label={({ category, count }) => { const cfg = categoryConfig[category] || categoryConfig.unknown; return `${cfg.label}: ${count}`; }}>{stats.categories.map((entry, index) => (<Cell key={`cell-${index}`} fill={(categoryConfig[entry.category] || categoryConfig.unknown).color} />))}</Pie><Tooltip formatter={(value, name) => [value, (categoryConfig[name] || categoryConfig.unknown).label]} /><Legend formatter={(value) => (categoryConfig[value] || categoryConfig.unknown).label} /></PieChart></ResponsiveContainer></CardContent>
          </Card>
        </div>
      ) : (
        <Card className="bg-[#EAE7DE] border-[#D2C2B3] mb-8"><CardContent className="p-8 text-center"><ChartBar size={48} weight="duotone" className="mx-auto mb-4 text-[#7C8068]" /><p className="text-[#7C8068]">No species data yet. Identify species in your entries to see statistics!</p><Button onClick={() => navigate("/entry/new")} className="mt-4 bg-[#55624F] hover:bg-[#424D3D] text-white">Create Entry</Button></CardContent></Card>
      )}

      {/* Top Species */}
      {stats?.top_species?.length > 0 && (
        <Card className="bg-white border-[#D2C2B3] mb-8"><CardHeader><CardTitle className="font-heading text-xl text-[#2E332B]">Most Sighted Species</CardTitle></CardHeader>
          <CardContent><div className="space-y-3">{stats.top_species.map((species, idx) => (<div key={species.name} className="flex items-center gap-2 sm:gap-4"><span className="text-lg font-semibold text-[#7C8068] w-6 shrink-0">{idx + 1}</span><div className="flex-1 min-w-0"><p className="font-medium text-[#2E332B] truncate">{species.name}</p></div><Badge className="bg-[#EAE7DE] text-[#55624F] shrink-0 text-xs whitespace-nowrap">{species.count} sighting{species.count > 1 ? 's' : ''}</Badge></div>))}</div></CardContent>
        </Card>
      )}

      {/* Custom Templates */}
      <div className="mt-8">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <h2 className="font-heading text-xl sm:text-2xl font-semibold text-white">My Custom Templates</h2>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate("/marketplace")} className="border-[#D2C2B3] text-[#55624F] text-xs sm:text-sm" data-testid="browse-marketplace-btn">
              <Storefront size={16} className="mr-1" /><span className="hidden sm:inline">Marketplace</span><span className="sm:hidden">Market</span>
            </Button>
            <Dialog open={showCreateTemplate} onOpenChange={setShowCreateTemplate}>
            <DialogTrigger asChild><Button size="sm" className="bg-[#55624F] hover:bg-[#424D3D] text-white text-xs sm:text-sm" data-testid="create-template-btn"><Plus size={16} className="mr-1" />Create</Button></DialogTrigger>
            <DialogContent className="bg-white border-[#D2C2B3] max-w-lg">
              <DialogHeader><DialogTitle className="font-heading text-xl">Create Custom Template</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-4">
                <div><label className="block text-sm font-medium text-[#55624F] mb-2">Template Name *</label><Input value={newTemplate.name} onChange={(e) => setNewTemplate(prev => ({ ...prev, name: e.target.value }))} placeholder="e.g., Beach Combing" className="bg-[#EAE7DE] border-[#D2C2B3]" /></div>
                <div><label className="block text-sm font-medium text-[#55624F] mb-2">Icon</label><div className="flex flex-wrap gap-2">{iconOptions.map(({ value, label, Icon }) => (<Button key={value} type="button" variant={newTemplate.icon === value ? "default" : "outline"} size="sm" onClick={() => setNewTemplate(prev => ({ ...prev, icon: value }))} className={newTemplate.icon === value ? "bg-[#55624F]" : "border-[#D2C2B3]"}><Icon size={16} /></Button>))}</div></div>
                <div><label className="block text-sm font-medium text-[#55624F] mb-2">Entry Title Prefix *</label><Input value={newTemplate.title} onChange={(e) => setNewTemplate(prev => ({ ...prev, title: e.target.value }))} placeholder="e.g., Beach Walk - " className="bg-[#EAE7DE] border-[#D2C2B3]" /></div>
                <div><label className="block text-sm font-medium text-[#55624F] mb-2">Content Template</label><Textarea value={newTemplate.content} onChange={(e) => setNewTemplate(prev => ({ ...prev, content: e.target.value }))} placeholder="Location:\n\nFinds:\n\nNotes:" rows={5} className="bg-[#EAE7DE] border-[#D2C2B3]" /></div>
                <div>
                  <label className="block text-sm font-medium text-[#55624F] mb-2">Default Tags</label>
                  <div className="flex gap-2 mb-2"><Input value={templateTagInput} onChange={(e) => setTemplateTagInput(e.target.value)} placeholder="Add tag" onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addTemplateTag())} className="flex-1 bg-[#EAE7DE] border-[#D2C2B3]" /><Button type="button" onClick={addTemplateTag} variant="outline" className="border-[#D2C2B3]"><Plus size={16} /></Button></div>
                  {newTemplate.tags.length > 0 && (<div className="flex flex-wrap gap-2">{newTemplate.tags.map(tag => (<Badge key={tag} className="bg-[#EAE7DE] text-[#2E332B] cursor-pointer" onClick={() => setNewTemplate(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }))}>{tag} <X size={12} className="ml-1" /></Badge>))}</div>)}
                </div>
                <div className="flex gap-3 pt-2"><Button variant="outline" onClick={() => setShowCreateTemplate(false)} className="flex-1 border-[#D2C2B3]">Cancel</Button><Button onClick={handleCreateTemplate} className="flex-1 bg-[#55624F] hover:bg-[#424D3D] text-white">Create Template</Button></div>
              </div>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {customTemplates.length === 0 ? (
          <Card className="bg-[#EAE7DE] border-[#D2C2B3]"><CardContent className="p-8 text-center"><FileText size={48} weight="duotone" className="mx-auto mb-4 text-[#7C8068]" /><p className="text-[#7C8068]">No custom templates yet. Create one to speed up your journaling!</p></CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {customTemplates.map(template => {
              const IconComponent = templateIcons[template.icon] || FileText;
              return (
                <Card key={template.id} className="bg-white border-[#D2C2B3]">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3"><div className="p-2 bg-[#EAE7DE] rounded-lg"><IconComponent size={24} weight="duotone" className="text-[#55624F]" /></div><div><p className="font-medium text-[#2E332B]">{template.name}</p><p className="text-xs text-[#7C8068]">{template.title}</p></div></div>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteTemplate(template.id)} className="text-[#C25A3C] hover:bg-[#C25A3C]/10"><Trash size={16} /></Button>
                    </div>
                    {template.tags?.length > 0 && (<div className="flex flex-wrap gap-1 mt-3">{template.tags.map(tag => (<Badge key={tag} className="bg-[#EAE7DE] text-[#7C8068] text-xs">{tag}</Badge>))}</div>)}
                    <Button variant="outline" size="sm" onClick={() => navigate(`/entry/new?template=${template.id}`)} className="w-full mt-3 border-[#D2C2B3] text-[#55624F]">Use Template</Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default StatisticsPage;
